import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, Siren, X } from "lucide-react";

const SEVERITY_STYLES = {
  amber: {
    bg: "bg-[#F59E0B]/15",
    border: "border-[#F59E0B]/60",
    text: "text-[#F59E0B]",
    label: "AMBER ALERT",
    icon: AlertTriangle,
  },
  red: {
    bg: "bg-[#EF4444]/15",
    border: "border-[#EF4444]/70",
    text: "text-[#EF4444]",
    label: "RED ALERT",
    icon: Siren,
  },
};

// Sticky global shock banner — pinned at the top of the dashboard.
// Polls /api/alerts/active every 30s. Shows the highest-severity alert
// first (red before amber). Dismissed alerts are remembered per session.
export default function GlobalShockBanner() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [items, setItems] = useState([]);
  const [hidden, setHidden] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem("tradeiq_shock_hidden") || "[]")); }
    catch { return new Set(); }
  });

  const load = async () => {
    try {
      const { data } = await api.get("/alerts/active");
      setItems(data.items || []);
    } catch {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const persistHidden = (next) => {
    try { sessionStorage.setItem("tradeiq_shock_hidden", JSON.stringify(Array.from(next))); } catch {}
  };

  const dismissForSession = (alertId) => {
    const next = new Set(hidden);
    next.add(alertId);
    setHidden(next);
    persistHidden(next);
  };

  const adminDismiss = async (alertId) => {
    try {
      await api.post(`/admin/alerts/dismiss/${alertId}`);
      await load();
    } catch {}
  };

  const visible = items.filter((a) => !hidden.has(a.alert_id));
  if (visible.length === 0) return null;

  return (
    <div className="sticky top-0 z-40 border-b border-zinc-900" data-testid="shock-banner">
      {visible.map((a) => {
        const s = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.amber;
        const Icon = s.icon;
        const pctTxt = a.last_trigger_pct > 0 ? `+${a.last_trigger_pct.toFixed(2)}%` : `${a.last_trigger_pct.toFixed(2)}%`;
        return (
          <div
            key={a.alert_id}
            className={`${s.bg} ${s.border} border-b px-4 md:px-6 py-2.5 flex items-center gap-3`}
            data-testid={`shock-alert-${a.severity}`}
          >
            <div className={`flex items-center gap-2 ${s.text}`}>
              <Icon className={`h-4 w-4 ${a.severity === "red" ? "animate-pulse" : ""}`} />
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] font-mono" data-testid={`shock-severity-${a.severity}`}>
                {s.label}
              </span>
            </div>
            <div className="text-xs md:text-sm text-zinc-200 leading-snug flex-1 min-w-0">
              <span className={`font-bold ${s.text} font-mono`} data-testid="shock-index">{a.index_name}</span>
              <span className="text-zinc-500"> · {a.country} · </span>
              <span className={`font-mono font-bold ${s.text}`} data-testid="shock-pct">{pctTxt}</span>
              <span className="text-zinc-400"> in {a.lookback_minutes}m </span>
              <span className="text-zinc-500 hidden md:inline"> · {a.message}</span>
            </div>
            <div className="text-[10px] font-mono text-zinc-500 hidden lg:block" title={new Date(a.triggered_at).toLocaleString()}>
              {new Date(a.last_trigger_at || a.triggered_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
              {a.trigger_count > 1 ? ` · ×${a.trigger_count}` : ""}
            </div>
            <div className="flex items-center gap-1">
              {isAdmin && (
                <button
                  onClick={() => adminDismiss(a.alert_id)}
                  className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-white px-2 py-1 border border-zinc-700 hover:border-zinc-500"
                  title="Admin: dismiss this alert for everyone"
                  data-testid={`shock-admin-dismiss-${a.alert_id}`}
                >
                  Admin clear
                </button>
              )}
              <button
                onClick={() => dismissForSession(a.alert_id)}
                className="text-zinc-500 hover:text-white p-1"
                title="Hide for this session"
                data-testid={`shock-dismiss-${a.alert_id}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
